import { ContentRenderer } from "@/lib/content/ContentRenderer";
import { QuestionTestStateSchema } from "@/types/redux-schema.js";
import { Text } from "@mantine/core";
import { Question } from "@tsTypes/question-schema";
import { TestStates } from "@tsTypes/test-schema";
import clsx from "clsx";
import QuestionOptions from "./MultipleChoiceQuestionOptions.tsx";
import classes from "./QuestionBody.module.css";
import QuestionTextArea from "./QuestionTextArea";
import Wrapper from "./Wrapper";

export const useQuestionBodyStyles = () => ({ classes });

export default function QuestionBody({
  questionDocumentId,
  question,
  questionMeta,
  handleAnswerInteraction,
  selectedAnswer,
  opened,
  marked,
  testStatus,
  questionTestState,
}: {
  questionDocumentId: string;
  question: Question["Question"];
  questionMeta: Question["QuestionStyle"][0];
  handleAnswerInteraction: (answer: string) => void;
  selectedAnswer: string;
  opened: boolean;
  marked: number | undefined;
  testStatus?: TestStates;
  questionTestState?: QuestionTestStateSchema;
}) {
  let bodyContent;
  switch (questionMeta.__component) {
    case "questions.text-options":
    case "questions.image-options":
      bodyContent = (
        <>
          <Text component="div">
            <ContentRenderer content={question} />
          </Text>
          <QuestionOptions
            options={
              questionMeta.__component === "questions.text-options"
                ? questionMeta.QuestionOptions!
                : questionMeta.Options!
            }
            handleAnswerInteraction={handleAnswerInteraction}
            selectedAnswer={selectedAnswer}
            marked={marked}
            images={
              questionMeta.__component === "questions.image-options"
                ? true
                : undefined
            }
          />
        </>
      );
      break;
    case "questions.long-extended-response":
    case "questions.long-single-part":
      bodyContent = (
        <div className={classes.questionStemLongAnswer}>
          <Text component="div">
            <ContentRenderer content={question} />
          </Text>
          <Text fw={500}>[{questionMeta.TotalMarks.TotalMarks}]</Text>
          <QuestionTextArea
            opened={opened}
            questionPartMarks={questionMeta.TotalMarks.TotalMarks}
            handleAnswerInteraction={handleAnswerInteraction}
            questionTestState={questionTestState}
            partId={questionDocumentId}
          />
        </div>
      );
      break;
    case "questions.long-multi-part":
      bodyContent = (
        <>
          <Text component="div">
            <ContentRenderer content={question} />
          </Text>
          <ol className={classes.questionPart}>
            {questionMeta.QuestionPart!.map((part) => (
              <li key={part.id}>
                <Text component="div" display={"inline"}>
                  <ContentRenderer content={part.QuestionPart} />
                </Text>
                <Text fw={500}>[{part.Marks}]</Text>
                <QuestionTextArea
                  opened={opened}
                  questionPartMarks={part.Marks}
                  handleAnswerInteraction={handleAnswerInteraction}
                  questionTestState={questionTestState}
                  partId={part.id}
                />
              </li>
            ))}
          </ol>
        </>
      );
      break;
  }
  return (
    <>
      {testStatus === "paused" && (
        <Text className={classes.testPausedNotice}>
          Start the test timer to see questions
        </Text>
      )}
      <Wrapper
        className={clsx(
          classes.questionWrapper,
          testStatus === "paused" && classes.testPausedStyles
        )}
      >
        {bodyContent}
      </Wrapper>
    </>
  );
}
